import {
  validateAppName,
  validateDisplayName,
  validateEnvVarKey,
  validateExternalUserId,
  type EndpointFit,
  type TargetMode,
} from './deployPlan.ts';
import { WIZARD_STEPS, type WizardStep } from './deployWizard.ts';
import { subdomainError } from './subdomain.ts';

export interface GatingInput {
  catalogId: number | null;
  stackName: string;
  endpointId: number | null;
  mode: TargetMode;
  selectedFit: EndpointFit | null; // fit of endpointId, if present in the list
  hasRecommended: boolean; // a recommended fit exists
  traefikBlocked: boolean;
  externalUserId: string;
  displayName: string;
  appName: string;
  useCustomName: boolean;
  instanceName: string;
  envVars: Readonly<Record<string, string>>;
  envDraftPending: boolean; // text typed in the add-variable row but not added
  subdomain: string; // '' when none, or when the app gets no hostname
}

export interface StepValidity {
  valid: Record<WizardStep, boolean>;
  reasons: Record<WizardStep, string | null>;
  firstInvalid: WizardStep | null;
  endpointGone: boolean;
}

export const REASON_SOURCE = 'Choose a catalog and a stack to continue';
export const REASON_TARGET = 'Choose an endpoint with enough resources';
export const REASON_ENDPOINT_GONE = 'The selected endpoint is no longer available. Choose another.';
export const REASON_TRAEFIK = 'Deploy Traefik on this endpoint first, or choose another.';
export const REASON_CONFIGURE = 'Fix the highlighted fields to continue';
export const REASON_ENV_DRAFT = 'Finish adding the environment variable, or clear it, to continue';

export function computeStepValidity(i: GatingInput): StepValidity {
  const endpointGone = i.endpointId !== null && i.selectedFit === null;

  const source = i.catalogId !== null && i.stackName !== '';
  const targetOk =
    i.endpointId !== null
      ? i.selectedFit !== null && i.selectedFit.sufficient && i.selectedFit.allowed
      : i.mode === 'auto' && i.hasRecommended;
  const target = targetOk && !i.traefikBlocked;
  const configure =
    validateExternalUserId(i.externalUserId) &&
    validateDisplayName(i.displayName) &&
    (i.appName === '' || validateAppName(i.appName)) &&
    (!i.useCustomName || (i.instanceName !== '' && validateAppName(i.instanceName))) &&
    subdomainError(i.subdomain) === null &&
    Object.keys(i.envVars).every(validateEnvVarKey) &&
    !i.envDraftPending;
  const review = source && target && configure;

  const valid: Record<WizardStep, boolean> = { source, target, configure, review };

  const reasons: Record<WizardStep, string | null> = {
    source: source ? null : REASON_SOURCE,
    target: target
      ? null
      : endpointGone
        ? REASON_ENDPOINT_GONE
        : i.traefikBlocked
          ? REASON_TRAEFIK
          : REASON_TARGET,
    configure: configure ? null : i.envDraftPending ? REASON_ENV_DRAFT : REASON_CONFIGURE,
    review: null,
  };

  const firstInvalid = WIZARD_STEPS.find((s) => s !== 'review' && !valid[s]) ?? null;
  reasons.review = firstInvalid ? reasons[firstInvalid] : null;

  return { valid, reasons, firstInvalid, endpointGone };
}
